// Composer —— 多行输入，Enter 发送，Shift+Enter 换行
// IME 组合期不触发发送（避免中文输入误发）
// K7: 二级菜单 —— stage 1 选 adapter id, stage 2 选其 usage 模板
//
// 重要: 所有 key handling 都在这一个 onKeyDown 里, 不让 MentionDropdown
// 自己 listen window —— 避免 native capture + React batch update 顺序导致
// "Enter 选中 mention 同时被当成 submit" 的 race.

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";

import type { RoomAdapter } from "../lib/auth-api";
import { useI18n } from "../lib/i18n";
import type { ChatAttachment } from "../lib/use-room";
import { useDialog } from "./Dialog";
import {
  filterAdapters,
  filterUsages,
  MentionDropdown,
  type MentionStage,
} from "./MentionDropdown";

interface ComposerProps {
  disabled?: boolean;
  placeholder?: string;
  /** K7: 完整 Room adapter 列表（带 usages 二级菜单） */
  adapters?: RoomAdapter[];
  /** 旧 API, 退化为 id-only adapter list */
  mentionSuggestions?: string[];
  onSend(text: string): void;
  onSendAttachment?: (attachment: ChatAttachment) => void;
}

export interface ComposerHandle {
  focusAgent(adapterId: string): void;
}

interface MentionState {
  stage: MentionStage;
  /** 当前输入 token 的起点 (含 @) */
  start: number;
  /** 当前 cursor 位置 */
  end: number;
  /** stage="adapter" 时是 @ 后输入的内容; stage="args" 时是子参数 token */
  query: string;
  /** stage="args" 时已选定的 adapter */
  adapter?: RoomAdapter;
}

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer({
  disabled,
  placeholder,
  adapters: roomAdapters,
  mentionSuggestions = [],
  onSend,
  onSendAttachment,
}: ComposerProps, ref) {
  const dlg = useDialog();
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [composing, setComposing] = useState(false);
  const [mention, setMention] = useState<MentionState | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recording, setRecording] = useState(false);
  const [speeching, setSpeeching] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  useEffect(() => {
    return () => {
      mediaRecorderRef.current?.stream.getTracks().forEach((track) => track.stop());
    };
  }, []);

  // 合并: server 返的 RoomAdapter 优先; 没有就退化为 mentionSuggestions
  const adapters: RoomAdapter[] = useMemo(() => {
    if (roomAdapters && roomAdapters.length > 0) return roomAdapters;
    return mentionSuggestions.map((id) => ({
      id,
      description: null,
      usages: [],
      trust_scope: "",
    }));
  }, [roomAdapters, mentionSuggestions]);

  // 候选数量 (Arrow/Enter 边界检查)
  const filteredCount = useMemo(() => {
    if (!mention) return 0;
    if (mention.stage === "adapter") {
      return filterAdapters(adapters, mention.query).length;
    }
    return filterUsages(mention.adapter?.usages ?? [], mention.query).length;
  }, [mention, adapters]);

  const submit = useCallback(() => {
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText("");
    setMention(null);
    requestAnimationFrame(() => {
      if (taRef.current) taRef.current.style.height = "auto";
    });
  }, [text, onSend]);

  const pickAdapter = useCallback(
    (adapterId: string) => {
      const ta = taRef.current;
      if (!ta || !mention) return;
      const before = text.slice(0, mention.start);
      const after = text.slice(mention.end);
      const insert = `@${adapterId} `;
      const next = before + insert + after;
      setText(next);
      const adapter = adapters.find((a) => a.id === adapterId);
      const caret = before.length + insert.length;
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(caret, caret);
        ta.style.height = "auto";
        ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
      });
      // 如果该 adapter 有二级菜单, 立刻进 stage 2
      if (adapter && adapter.usages.length > 0) {
        setMention({
          stage: "args",
          start: caret,
          end: caret,
          query: "",
          adapter,
        });
        setActiveIndex(0);
      } else {
        setMention(null);
      }
    },
    [text, mention, adapters],
  );

  const pickUsage = useCallback(
    (template: string) => {
      const ta = taRef.current;
      if (!ta || !mention || mention.stage !== "args") return;
      const before = text.slice(0, mention.start);
      const after = text.slice(mention.end);
      const next = before + template + after;
      setText(next);
      setMention(null);
      const caret = before.length + template.length;
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(caret, caret);
        ta.style.height = "auto";
        ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
      });
    },
    [text, mention],
  );

  useImperativeHandle(
    ref,
    () => ({
      focusAgent(adapterId: string) {
        const prefix = `@${adapterId} `;
        setText((prev) => {
          const trimmed = prev.trimStart();
          if (trimmed.startsWith(prefix)) return prev;
          if (!prev.trim()) return `${prefix}interactive `;
          return `${prefix}interactive ${prev.trim()}`;
        });
        setMention(null);
        requestAnimationFrame(() => {
          const ta = taRef.current;
          if (!ta) return;
          ta.focus();
          const caret = ta.value.length;
          ta.setSelectionRange(caret, caret);
          ta.style.height = "auto";
          ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
        });
      },
    }),
    [],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention) {
      if (e.key === "ArrowDown") {
        if (filteredCount > 0) {
          e.preventDefault();
          setActiveIndex((i) => (i + 1) % filteredCount);
        }
        return;
      }
      if (e.key === "ArrowUp") {
        if (filteredCount > 0) {
          e.preventDefault();
          setActiveIndex((i) => (i - 1 + filteredCount) % filteredCount);
        }
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        if (filteredCount > 0) {
          e.preventDefault();
          if (mention.stage === "adapter") {
            const items = filterAdapters(adapters, mention.query);
            const pick = items[Math.min(activeIndex, items.length - 1)];
            if (pick) pickAdapter(pick.id);
          } else {
            const items = filterUsages(mention.adapter?.usages ?? [], mention.query);
            const pick = items[Math.min(activeIndex, items.length - 1)];
            if (pick) pickUsage(pick.template);
          }
        } else {
          if (e.key === "Enter" && !composing && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMention(null);
        return;
      }
      // 其他按键透传, 由 onInput 重新 detect
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !composing) {
      e.preventDefault();
      submit();
    }
  };

  /** 检测当前光标位置是否处于 mention 上下文 */
  const detectMention = (ta: HTMLTextAreaElement) => {
    const v = ta.value;
    const cur = ta.selectionStart ?? v.length;
    // 从光标向左找 @ 或 换行
    let i = cur - 1;
    while (i >= 0) {
      const c = v[i];
      if (c === "\n") {
        setMention(null);
        return;
      }
      if (c === "@") {
        const prev = i > 0 ? v[i - 1] : "";
        if (prev !== "" && !/\s/.test(prev)) {
          // @ 前不是空白, 不是 mention (像 email)
          setMention(null);
          return;
        }
        const after = v.slice(i + 1, cur);
        const sp = after.indexOf(" ");
        if (sp === -1) {
          // stage 1: @<query> 还没空格
          setMention({
            stage: "adapter",
            start: i,
            end: cur,
            query: after,
          });
          setActiveIndex(0);
          return;
        }
        // stage 2 候选: @adapter <args-query>
        const adapterId = after.slice(0, sp);
        const argsQuery = after.slice(sp + 1);
        // args 区内不能再有空格, 否则视为离开 mention
        if (argsQuery.includes(" ") || argsQuery.includes("\n")) {
          setMention(null);
          return;
        }
        const adapter = adapters.find((a) => a.id === adapterId);
        if (adapter && adapter.usages.length > 0) {
          setMention({
            stage: "args",
            start: i + 1 + sp + 1,
            end: cur,
            query: argsQuery,
            adapter,
          });
          setActiveIndex(0);
        } else {
          setMention(null);
        }
        return;
      }
      i--;
    }
    setMention(null);
  };

  const onInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    setText(ta.value);
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
    detectMention(ta);
  };

  const onSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    detectMention(e.currentTarget);
  };

  async function pickFiles(files: FileList | null) {
    if (!files || !onSendAttachment) return;
    for (const file of Array.from(files)) {
      const attachment = await fileToAttachment(file);
      if (attachment) onSendAttachment(attachment);
    }
    if (fileRef.current) fileRef.current.value = "";
  }

  async function toggleRecording() {
    if (!onSendAttachment) return;
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    // K11/M2 fix: 桌面端 (Tauri) 之前点这个无反应, 因为 Info.plist 没声明
    // NSMicrophoneUsageDescription, macOS 直接 reject getUserMedia, 又
    // 没 try/catch -> promise 静默吃掉. 现在加 try/catch + Dialog 提示.
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const msg = String(err);
      void dlg.alert({
        title: "无法访问麦克风",
        message:
          msg.includes("NotAllowed") || msg.includes("denied")
            ? "请到 系统设置 → 隐私与安全性 → 麦克风 里打开 ChatBox 的权限, 然后重启 ChatBox 再试。"
            : msg.includes("NotFound")
              ? "没找到可用的麦克风设备。"
              : `录音失败：${msg}`,
      });
      return;
    }
    const recorder = new MediaRecorder(stream);
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = async () => {
      const mime = recorder.mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: mime });
      stream.getTracks().forEach((track) => track.stop());
      setRecording(false);
      onSendAttachment({
        kind: "audio",
        name: `voice-${new Date().toISOString().replace(/[:.]/g, "-")}.webm`,
        mime,
        dataUrl: await blobToDataUrl(blob),
        size: blob.size,
      });
    };
    mediaRecorderRef.current = recorder;
    recorder.start();
    setRecording(true);
  }

  function startSpeechToText() {
    const SpeechRecognition =
      (window as unknown as { SpeechRecognition?: SpeechRecognitionCtor }).SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: SpeechRecognitionCtor }).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      // WKWebView (Tauri macOS 桌面端) 不实现 SpeechRecognition;
      // 浏览器版 Chrome / Safari 才有. 之前 silent return → UI 看着没响应.
      void dlg.alert({
        title: "当前环境不支持语音转文字",
        message:
          "桌面端的内嵌浏览器 (WebKit) 没有实现 SpeechRecognition API。可以改用'发语音'录音后再发送, 或在 Chrome 浏览器里打开 web 版使用语音转文字。",
      });
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "zh-CN";
    recognition.interimResults = true;
    recognition.continuous = false;
    setSpeeching(true);
    recognition.onresult = (event) => {
      const value = Array.from(event.results)
        .map((r) => r[0]?.transcript ?? "")
        .join("");
      setText(value);
    };
    recognition.onend = () => setSpeeching(false);
    recognition.onerror = (e) => {
      setSpeeching(false);
      const msg = e.error ?? "unknown";
      if (msg === "not-allowed" || msg === "service-not-allowed") {
        void dlg.alert({
          title: "麦克风权限被拒",
          message: "请在系统设置里给 ChatBox 开麦克风权限后重试。",
        });
      }
    };
    recognition.start();
  }

  const placeholderText = useMemo(
    () =>
      placeholder ??
      (disabled ? t("composer.placeholder.connecting") : t("composer.placeholder")),
    [placeholder, disabled, t],
  );

  return (
    <form
      className="composer"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <input
        ref={fileRef}
        type="file"
        hidden
        multiple
        accept="image/*,audio/*,.html,.htm,text/html"
        onChange={(e) => void pickFiles(e.currentTarget.files)}
      />
      <div className="composer__tools">
        <button type="button" className="composer__tool" disabled={disabled} onClick={startSpeechToText}>
          {speeching ? t("composer.tool.stt.recording") : t("composer.tool.stt")}
        </button>
        <button type="button" className="composer__tool" disabled={disabled || !onSendAttachment} onClick={() => void toggleRecording()}>
          {recording ? t("composer.tool.voice.recording") : t("composer.tool.voice")}
        </button>
        <button type="button" className="composer__tool" disabled={disabled || !onSendAttachment} onClick={() => fileRef.current?.click()}>
          {t("composer.tool.attachment")}
        </button>
      </div>
      <div className="composer__input-row">
        <div className="composer__wrap">
          {mention && (
            <MentionDropdown
              stage={mention.stage}
              query={mention.query}
              argsQuery={mention.query}
              adapters={adapters}
              activeAdapter={mention.adapter}
              active={activeIndex}
              onActiveChange={setActiveIndex}
              onPick={(v) =>
                mention.stage === "adapter" ? pickAdapter(v) : pickUsage(v)
              }
            />
          )}
          <textarea
            ref={taRef}
            rows={1}
            value={text}
            onChange={onInput}
            onKeyDown={onKeyDown}
            onSelect={onSelect}
            onClick={onSelect}
            onCompositionStart={() => setComposing(true)}
            onCompositionEnd={() => setComposing(false)}
            placeholder={placeholderText}
            disabled={disabled}
            autoFocus
          />
        </div>
        <button
          type="submit"
          className="composer__send"
          disabled={disabled || text.trim().length === 0}
          aria-label={t("composer.send")}
        >
          {t("composer.send")}
        </button>
      </div>
    </form>
  );
});

type SpeechRecognitionCtor = new () => {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  start(): void;
};

interface SpeechRecognitionEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}

async function fileToAttachment(file: File): Promise<ChatAttachment | null> {
  const dataUrl = await blobToDataUrl(file);
  if (file.type.startsWith("image/")) {
    return { kind: "image", name: file.name, mime: file.type, dataUrl, size: file.size };
  }
  if (file.type.startsWith("audio/")) {
    return { kind: "audio", name: file.name, mime: file.type, dataUrl, size: file.size };
  }
  if (file.type === "text/html" || /\.(html?|xhtml)$/i.test(file.name)) {
    return { kind: "html", name: file.name, mime: "text/html", dataUrl, size: file.size };
  }
  return null;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
